import React, { useEffect, useImperativeHandle, useRef } from 'react';
import { ReactComponent as ChevronRight } from '../../../assets/icons/chevron-right.svg';
import { Link } from 'react-router-dom';
import { AssetProposal } from '../hooks/assetProposalUtils';
import { useAssetProposalState } from '../hooks/useAssetProposalState';
import GroupSelector from './GroupSelector';
import AssetGroupEditor from './AssetGroupEditor';
import AssetEditRow from './AssetEditRow';

export interface AssetProposalTriggerHandle {
  approve: () => Promise<void>;
}

interface AssetProposalTriggerProps {
  proposal: AssetProposal;
  conversationId: string;
  messageId: string;
  disabled?: boolean;
}

const AssetProposalTrigger = React.forwardRef<AssetProposalTriggerHandle, AssetProposalTriggerProps>(
  ({ proposal, conversationId, messageId, disabled = false }, ref) => {
  const {
    status,
    isSettled,
    isAddMode,
    isSubmitting,
    submitError,
    approvedGroupId,
    groupHintNotFound,
    groupHintName,
    selectedGroupId,
    setSelectedGroupId,
    isGroupListOpen,
    setIsGroupListOpen,
    isAssetsOpen,
    setIsAssetsOpen,
    groupImageUrls,
    isGroupExpanded,
    setIsGroupExpanded,
    groupEdit,
    setGroupEdit,
    expandedAssetIndex,
    setExpandedAssetIndex,
    assetEdits,
    setAssetEdits,
    groups,
    groupList,
    handleOpenImageUpload,
    handleApprove,
    handleDecline,
  } = useAssetProposalState(proposal, conversationId, messageId, disabled);

  // Keep a ref to handleApprove so the imperative handle always calls the latest version (which closes over the current assetEdits/groupEdit state) without needing to reset the handle on every render.
  const handleApproveRef = useRef(handleApprove);
  useEffect(() => { handleApproveRef.current = handleApprove; });
  useImperativeHandle(ref, () => ({ approve: () => handleApproveRef.current() }), []);

  return (
    <div className="eai-panel mt-3 flex flex-col gap-4 overflow-hidden">
      {isAddMode && groupHintNotFound && !selectedGroupId && !isSettled && (
        <div className="rounded-eai-card border border-eai-warning-text/25 bg-eai-brand-peach px-3 py-2">
          <p className="text-eai-body-xs text-eai-ink">
            I couldn't find a group named <span className="font-semibold">"{groupHintName}"</span>. Select one of your groups below, or ask me to create it first.
          </p>
        </div>
      )}

      {isAddMode ? (
        <GroupSelector
          selectedGroupId={selectedGroupId}
          setSelectedGroupId={setSelectedGroupId}
          isGroupListOpen={isGroupListOpen}
          setIsGroupListOpen={setIsGroupListOpen}
          isSettled={isSettled}
          disabled={disabled}
          groups={groups}
          groupList={groupList}
          groupImageUrls={groupImageUrls}
        />
      ) : (
        <AssetGroupEditor
          groupEdit={groupEdit}
          onGroupEditChange={setGroupEdit}
          isGroupExpanded={isGroupExpanded}
          onToggleGroupExpanded={() => setIsGroupExpanded((o) => !o)}
          isSettled={isSettled}
          disabled={disabled}
        />
      )}

      <div>
        <button
          type="button"
          onClick={() => setIsAssetsOpen((o) => !o)}
          className="flex items-center gap-1 mb-2"
        >
          <p className="text-xs font-semibold text-eai-ink-faint uppercase tracking-wide">
            Assets ({proposal.assets.length})
          </p>
          <ChevronRight className={`w-3 h-3 flex-shrink-0 text-eai-ink-faint transition-transform ${isAssetsOpen ? 'rotate-90' : ''}`} />
        </button>
        {isAssetsOpen && (
          <div className="divide-y divide-eai-border">
            {proposal.assets.map((asset, index) => (
              <AssetEditRow
                key={index}
                asset={asset}
                edit={assetEdits[index]}
                isExpanded={expandedAssetIndex === index}
                isSettled={isSettled}
                onToggle={() => setExpandedAssetIndex(expandedAssetIndex === index ? null : index)}
                onEditName={(name) => setAssetEdits((prev) => {
                  const next = [...prev];
                  next[index] = { ...next[index], name };
                  return next;
                })}
                onEditDescription={(description) => setAssetEdits((prev) => {
                  const next = [...prev];
                  next[index] = { ...next[index], description };
                  return next;
                })}
                onOpenImageUpload={() => handleOpenImageUpload(index)}
              />
            ))}
          </div>
        )}
      </div>

      {submitError && (
        <p className="text-xs text-eai-danger-text">{submitError}</p>
      )}

      {approvedGroupId && (
        <Link
          to={`/home/group/${approvedGroupId}`}
          className="inline-flex items-center gap-1 text-xs font-medium text-eai-cta-primary hover:underline"
        >
          View Group
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      )}

      <div className="border-t border-eai-border pt-4">
        <p className="text-xs text-eai-ink-subtle mb-3">
          {isAddMode
            ? `Would you like to add ${proposal.assets.length} asset${proposal.assets.length !== 1 ? 's' : ''} to the selected group?`
            : <>Would you like me to create the asset group <span className="font-semibold text-eai-ink-soft">{proposal.assetGroup?.name}</span> with {proposal.assets.length} asset{proposal.assets.length !== 1 ? 's' : ''}?</>
          }
        </p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={handleApprove}
            disabled={isSubmitting || isSettled || disabled || (isAddMode && !selectedGroupId)}
            className={`eai-btn-cta-toolbar ${status === 'approved' ? 'cursor-default opacity-60' : ''}`}
          >
            {isSubmitting && (
              <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            )}
            {status === 'approved' ? '✓ Approved' : 'Approve'}
          </button>
          <button
            onClick={handleDecline}
            disabled={isSubmitting || isSettled || disabled}
            className={`eai-btn-toolbar ${status === 'declined' ? 'cursor-default opacity-60' : ''}`}
          >
            {status === 'declined' ? 'Declined' : 'Decline'}
          </button>
        </div>
      </div>
    </div>
  );
});

export default AssetProposalTrigger;
